import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import { getImageUrl } from "../config";
import { streamChat, EnergyBotApiError } from "../api/energyBot";
import RecaptchaComponent from "./RecaptchaComponent";
import "../css/energy-ai-bot.less";

// Configure marked parser options for GFM & line breaks
marked.setOptions({
  gfm: true,
  breaks: true,
});

interface Message {
  id: string;
  sender: "bot" | "user";
  text: string;
  timestamp: string;
  isQuickQuestions?: boolean;
}

interface Particle {
  x: number;
  y: number;
  baseRadius: number;
  angle: number;
  orbitRadius: number;
  speed: number;
  alpha: number;
  color: string;
  pulsePhase: number;
  vx?: number;
  vy?: number;
}

const EnergyAiBot: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isBackdropActive, setIsBackdropActive] = useState(false);
  const [isBackdropClosing, setIsBackdropClosing] = useState(false);
  const [isPanelClosing, setIsPanelClosing] = useState(false);
  const [isGnTrailActive, setIsGnTrailActive] = useState(false);
  const [gnTrailKey, setGnTrailKey] = useState<number>(0);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [transparentLogoUrl, setTransparentLogoUrl] = useState<string>("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isSessionVerified, setIsSessionVerified] = useState<boolean>(false);

  const orbCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const backdropCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const gnTrailCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const gnTrailTimerRef = useRef<NodeJS.Timeout | null>(null);
  const gnTrailIsClosingRef = useRef<boolean>(false);
  const recaptchaRef = useRef<{ reset: () => void } | null>(null);
  const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const animFrameId = useRef<number | null>(null);

  // Dynamically remove white/gray/checkerboard background from WE AI logo image
  useEffect(() => {
    const rawSrc = getImageUrl("/image/scientific/downloaded-image.png");
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = rawSrc;
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // Key out light gray/white/checkerboard pixels (r>170, g>170, b>170)
          if (r > 170 && g > 170 && b > 170 && Math.abs(r - g) < 25 && Math.abs(g - b) < 25) {
            data[i + 3] = 0; // Alpha 0
          }
        }
        ctx.putImageData(imgData, 0, 0);
        setTransparentLogoUrl(canvas.toDataURL("image/png"));
      } catch (err) {
        setTransparentLogoUrl(rawSrc);
      }
    };
    img.onerror = () => {
      setTransparentLogoUrl(rawSrc);
    };
  }, []);

  // Quick preset questions (2 items)
  const getQuickQuestions = useCallback(() => [
    { key: "q1", q: t("energy_bot.q1") },
    { key: "q2", q: t("energy_bot.q2") },
  ], [t]);

  // Initialize initial message
  useEffect(() => {
    const formattedTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages([
      {
        id: "welcome-1",
        sender: "bot",
        text: t("energy_bot.welcome"),
        timestamp: formattedTime,
        isQuickQuestions: true,
      },
    ]);
  }, [t]);

  // Auto-scroll chat body
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  /* ---------------- Particle Canvas Render Logic ---------------- */
  useEffect(() => {
    const canvas = orbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const size = 160;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const cx = size / 2;
    const cy = size / 2;

    // Create orbiting particles in vibrant Red & Blue (8 elegant particles with wider orbit)
    const particleCount = 8;
    const particles: Particle[] = [];
    const colors = ["#ff3b5c", "#00f0ff", "#ff4d6d", "#00c8ff"];

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.5;
      const orbitRadius = 24 + Math.random() * 24;
      particles.push({
        x: cx + Math.cos(angle) * orbitRadius,
        y: cy + Math.sin(angle) * orbitRadius,
        baseRadius: 1.5 + Math.random() * 2.0,
        angle,
        orbitRadius,
        speed: (0.006 + Math.random() * 0.01) * (i % 2 === 0 ? 1 : -1),
        alpha: 0.5 + Math.random() * 0.5,
        color: colors[i % colors.length],
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }

    let pulseRingR = 18;
    let pulseRingAlpha = 0.9;
    let pulseRingR2 = 45;
    let pulseRingAlpha2 = 0.45;
    let burstProgress = 0;

    // Static helper function to render liquid water ripple waves (avoids function allocation per frame)
    const drawWaterRipple = (r: number, alpha: number, rColor: "cyan" | "magenta") => {
      if (alpha <= 0 || r <= 15) return;
      const progress = Math.min(1, Math.max(0, (r - 18) / 56));
      const waveWidth = 4 + progress * 12;

      const innerR = Math.max(0.1, r - waveWidth / 2);
      const outerR = r + waveWidth / 2;

      const waveGrad = ctx.createRadialGradient(cx, cy, innerR, cx, cy, outerR);
      const baseAlpha = alpha * 0.8;

      if (rColor === "cyan") {
        waveGrad.addColorStop(0, "rgba(0, 240, 255, 0)");
        waveGrad.addColorStop(0.25, `rgba(224, 255, 255, ${baseAlpha * 0.35})`);
        waveGrad.addColorStop(0.75, `rgba(0, 240, 255, ${baseAlpha * 0.95})`);
        waveGrad.addColorStop(1, "rgba(0, 240, 255, 0)");
      } else {
        waveGrad.addColorStop(0, "rgba(255, 59, 92, 0)");
        waveGrad.addColorStop(0.25, `rgba(255, 160, 180, ${baseAlpha * 0.3})`);
        waveGrad.addColorStop(0.75, `rgba(255, 59, 92, ${baseAlpha * 0.9})`);
        waveGrad.addColorStop(1, "rgba(255, 59, 92, 0)");
      }

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.strokeStyle = waveGrad;
      ctx.lineWidth = waveWidth;
      ctx.stroke();
    };

    const render = () => {
      ctx.clearRect(0, 0, size, size);

      if (isTransitioning) {
        // Burst dispersion & trajectory trail animation on click
        burstProgress += 0.04;
        particles.forEach((p) => {
          if (p.vx === undefined || p.vy === undefined) {
            const centerAngle = Math.PI * 1.25 + (Math.random() - 0.5) * 0.9;
            const speed = 5 + Math.random() * 7;
            p.vx = Math.cos(centerAngle) * speed;
            p.vy = Math.sin(centerAngle) * speed;
          }
          p.x += p.vx;
          p.y += p.vy;
          p.alpha = Math.max(0, 1 - burstProgress);

          // Draw particle head
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.baseRadius * (1 + burstProgress * 0.5), 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.alpha;
          ctx.shadowBlur = 12;
          ctx.shadowColor = p.color;
          ctx.fill();

          // Draw particle comet tail line
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - p.vx * 5, p.y - p.vy * 5);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = p.baseRadius;
          ctx.globalAlpha = p.alpha * 0.7;
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        });

        if (burstProgress < 1) {
          animFrameId.current = requestAnimationFrame(render);
        }
        return;
      }

      // Normal idle sci-fi energy core render
      // 1. Double Radar expanding pulse rings (Significantly LARGER radius: 18px -> 74px, much SLOWER: +0.09px/frame)
      pulseRingR += 0.09;
      pulseRingAlpha = 0.85 * (1 - (pulseRingR - 18) / 56);
      if (pulseRingR > 74) {
        pulseRingR = 18;
        pulseRingAlpha = 0.85;
      }

      pulseRingR2 += 0.09;
      pulseRingAlpha2 = 0.85 * (1 - (pulseRingR2 - 18) / 56);
      if (pulseRingR2 > 74) {
        pulseRingR2 = 18;
        pulseRingAlpha2 = 0.85;
      }

      // Draw Liquid Water Ripples
      drawWaterRipple(pulseRingR, pulseRingAlpha, "cyan");
      drawWaterRipple(pulseRingR2, pulseRingAlpha2, "magenta");

      // 2. Central Energy Core Radial Gradient
      const corePulse = Math.sin(Date.now() * 0.0012) * 4;
      const coreGrad = ctx.createRadialGradient(cx, cy, 2, cx, cy, 28 + corePulse);
      coreGrad.addColorStop(0, "rgba(255, 255, 255, 0.98)");
      coreGrad.addColorStop(0.35, "rgba(0, 240, 255, 0.45)");
      coreGrad.addColorStop(0.7, "rgba(255, 59, 92, 0.25)");
      coreGrad.addColorStop(1, "rgba(0, 240, 255, 0)");

      ctx.beginPath();
      ctx.arc(cx, cy, 28 + corePulse, 0, Math.PI * 2);
      ctx.fillStyle = coreGrad;
      ctx.shadowBlur = 22;
      ctx.shadowColor = "#00f0ff";
      ctx.fill();

      // 3. Connect energy lines between close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 30) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 240, 255, ${0.45 * (1 - dist / 30)})`;
            ctx.lineWidth = 0.9;
            ctx.stroke();
          }
        }
      }

      // 4. Update and render particles
      particles.forEach((p) => {
        p.angle += p.speed;
        p.pulsePhase += 0.018;
        const wave = Math.sin(p.pulsePhase) * 3.5;
        p.x = cx + Math.cos(p.angle) * (p.orbitRadius + wave);
        p.y = cy + Math.sin(p.angle) * (p.orbitRadius + wave);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.baseRadius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.fill();
        ctx.globalAlpha = 1.0;
      });

      animFrameId.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animFrameId.current) {
        cancelAnimationFrame(animFrameId.current);
      }
    };
  }, [isTransitioning, isOpen]);

  /* ---------------- Panel Holographic Background Canvas ---------------- */
  useEffect(() => {
    if (!isOpen) return;
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = 380;
    canvas.height = 540;

    // Low-density floating particles inside panel (8 particles max, fluorescent starry white)
    const particleCount = 8;
    const nodes: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      baseColor: string;
      glowColor: string;
      alpha: number;
      phase: number;
    }[] = [];

    const starColors = [
      { fill: "rgba(255, 255, 255, ", glow: "#ffffff" },
      { fill: "rgba(224, 255, 255, ", glow: "#e0ffff" },
      { fill: "rgba(240, 248, 255, ", glow: "#f0f8ff" },
    ];

    for (let i = 0; i < particleCount; i++) {
      const star = starColors[i % starColors.length];
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        radius: 1.2 + Math.random() * 2.2,
        baseColor: star.fill,
        glowColor: star.glow,
        alpha: 0.3 + Math.random() * 0.4,
        phase: Math.random() * Math.PI * 2,
      });
    }

    let bgFrameId: number;
    const renderBg = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        n.phase += 0.025;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;

        const currentAlpha = Math.max(0.15, n.alpha + Math.sin(n.phase) * 0.2);

        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fillStyle = `${n.baseColor}${currentAlpha})`;
        ctx.shadowBlur = 12;
        ctx.shadowColor = n.glowColor;
        ctx.fill();
      });

      bgFrameId = requestAnimationFrame(renderBg);
    };

    renderBg();

    return () => {
      cancelAnimationFrame(bgFrameId);
    };
  }, [isOpen]);

  /* ---------------- Fullscreen Backdrop Floating Particle Canvas ---------------- */
  useEffect(() => {
    if (!isBackdropActive) return;
    const canvas = backdropCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const particleCount = 28;
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      color: string;
      glowColor: string;
      alpha: number;
      phase: number;
    }[] = [];

    const starColors = [
      { fill: "rgba(255, 255, 255, ", glow: "#ffffff" },
      { fill: "rgba(224, 255, 255, ", glow: "#e0ffff" },
      { fill: "rgba(240, 248, 255, ", glow: "#f0f8ff" },
    ];

    for (let i = 0; i < particleCount; i++) {
      const col = starColors[i % starColors.length];
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: 1.2 + Math.random() * 2.5,
        color: col.fill,
        glowColor: col.glow,
        alpha: 0.25 + Math.random() * 0.45,
        phase: Math.random() * Math.PI * 2,
      });
    }

    let frameId: number;
    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Constellation lines between nearby fluorescent white star particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(224, 255, 255, ${0.14 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      // Update and render floating starry white particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.phase += 0.025;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        const currentAlpha = Math.max(0.15, p.alpha + Math.sin(p.phase) * 0.25);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${currentAlpha})`;
        ctx.shadowBlur = 14;
        ctx.shadowColor = p.glowColor;
        ctx.fill();
      });

      frameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
    };
  }, [isBackdropActive]);

  // Helper to safely trigger/re-trigger GN particle trail animation and clear previous timer
  const triggerGnTrail = useCallback((durationMs: number, isClosing: boolean = false) => {
    if (gnTrailTimerRef.current) {
      clearTimeout(gnTrailTimerRef.current);
    }
    gnTrailIsClosingRef.current = isClosing;
    setIsGnTrailActive(true);
    setGnTrailKey((prev) => prev + 1);

    gnTrailTimerRef.current = setTimeout(() => {
      setIsGnTrailActive(false);
      gnTrailTimerRef.current = null;
    }, durationMs);
  }, []);

  /* ---------------- Gundam 00 GN Particle Thruster Stream Engine ---------------- */
  useEffect(() => {
    if (!isGnTrailActive || gnTrailKey === 0) return;

    // Lock flight direction upon activation from ref (avoids reactivity loop & ESLint warnings)
    const isClosing = gnTrailIsClosingRef.current;

    const canvas = gnTrailCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Positions: Trigger button center <-> Screen Center
    const startX = width - 68;
    const startY = height - 68;
    const targetX = width / 2;
    const targetY = height / 2;

    const fromX = isClosing ? targetX : startX;
    const fromY = isClosing ? targetY : startY;
    const toX = isClosing ? startX : targetX;
    const toY = isClosing ? startY : targetY;

    // GN Particle Pool
    interface GNParticle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      maxSize: number;
      alpha: number;
      life: number;
      maxLife: number;
      color: string;
      glowColor: string;
      sparkle: boolean;
    }

    const gnParticles: GNParticle[] = [];
    const gnColors = [
      { core: "rgba(255, 59, 92, 0.95)", glow: "#ff3b5c" }, // Vibrant Sci-Fi Red
      { core: "rgba(0, 240, 255, 0.95)", glow: "#00f0ff" }, // Vibrant Electric Blue / Cyan
      { core: "rgba(255, 77, 109, 0.95)", glow: "#ff4d6d" }, // Coral Red
      { core: "rgba(0, 200, 255, 0.95)", glow: "#00c8ff" }, // Deep Cyan
    ];

    let frameId: number;
    const startTime = performance.now();
    const duration = isClosing ? 380 : 420; // ms

    const renderGNStream = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      // Easing flight curve
      const easeP = isClosing
        ? Math.pow(progress, 2)
        : 1 - Math.pow(1 - progress, 3);

      // Current head position of window trajectory
      const headX = fromX + (toX - fromX) * easeP;
      const headY = fromY + (toY - fromY) * easeP;

      ctx.clearRect(0, 0, width, height);

      // 1. Emit 3 new red/blue particles per frame during flight trajectory
      if (progress < 0.95) {
        const flightAngle = Math.atan2(toY - fromY, toX - fromX);
        const emitCount = 3; // Sparser, cleaner particle count

        for (let i = 0; i < emitCount; i++) {
          const spreadAngle = flightAngle + Math.PI + (Math.random() - 0.5) * 2.8; // Wider dispersion angle
          const speed = 1.2 + Math.random() * 4.2; // Dispersion speed
          const colorObj = gnColors[Math.floor(Math.random() * gnColors.length)];
          const maxLife = 70 + Math.random() * 60; // Floating lifetime

          gnParticles.push({
            x: headX + (Math.random() - 0.5) * 60, // Emission offset
            y: headY + (Math.random() - 0.5) * 60,
            vx: Math.cos(spreadAngle) * speed + (Math.random() - 0.5) * 1.0,
            vy: Math.sin(spreadAngle) * speed + (Math.random() - 0.5) * 1.0,
            size: 1.5 + Math.random() * 1.5,
            maxSize: 2.0 + Math.random() * 2.0,
            alpha: 0.95 + Math.random() * 0.05,
            life: 0,
            maxLife,
            color: colorObj.core,
            glowColor: colorObj.glow,
            sparkle: Math.random() > 0.4,
          });
        }
      }

      // 2. Update & Render active floating GN particles over black backdrop
      for (let i = gnParticles.length - 1; i >= 0; i--) {
        const p = gnParticles[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.982; // Very gentle drag slowdown
        p.vy *= 0.982;

        const lifeRatio = p.life / p.maxLife;
        // Smooth sinusoidal fade out so particles linger brightly on black backdrop then dissolve
        p.alpha = Math.max(0, Math.sin((1 - lifeRatio) * Math.PI * 0.5));
        p.size = p.maxSize * (1 - lifeRatio * 0.25);

        if (p.life >= p.maxLife || p.alpha <= 0) {
          gnParticles.splice(i, 1);
          continue;
        }

        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.shadowBlur = p.sparkle ? 22 : 14;
        ctx.shadowColor = p.glowColor;
        ctx.fill();

        // Inner bright white core dot
        if (p.size > 2.5) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 0.45, 0, Math.PI * 2);
          ctx.fillStyle = "#ffffff";
          ctx.fill();
        }
        ctx.restore();
      }

      if (progress < 1 || gnParticles.length > 0) {
        frameId = requestAnimationFrame(renderGNStream);
      }
    };

    frameId = requestAnimationFrame(renderGNStream);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [gnTrailKey, isGnTrailActive]);

  /* ---------------- Sequential Open / Close Handlers ---------------- */
  const handleOpen = () => {
    if (isOpen || isTransitioning || isPanelClosing || isBackdropClosing) return;

    // Step 1: Trigger bot orb burst & GN particle stream emergence
    setIsTransitioning(true);
    setIsOpen(true);
    triggerGnTrail(2400, false);

    // Step 2: Chat panel emerges to center (takes ~420ms).
    // AFTER chat panel completely opens, expand black backdrop wave outward.
    setTimeout(() => {
      setIsTransitioning(false);
      setIsBackdropActive(true);
    }, 420);
  };

  const handleClose = () => {
    if (isBackdropClosing || isPanelClosing || !isOpen) return;

    // Step 1: Black backdrop retracts FIRST (takes 800ms), while chat panel stays 100% open at center.
    setIsBackdropClosing(true);

    setTimeout(() => {
      // Step 2: AFTER black backdrop has completely finished retracting back into center, hide backdrop
      setIsBackdropActive(false);
      setIsBackdropClosing(false);

      // Step 3: NOW shrink chat panel back to bot orb (takes ~380ms)
      setIsPanelClosing(true);
      triggerGnTrail(2000, true);

      setTimeout(() => {
        // Step 4: AFTER chat panel has completely shrunk into bot orb, reset state
        setIsOpen(false);
        setIsPanelClosing(false);
      }, 380);
    }, 800);
  };

  /* ---------------- SSE Streaming API Chat (Real API Integration) ---------------- */
  const streamingAbortRef = useRef<AbortController | null>(null);

  // Generate a stable session_id for the lifetime of the component
  const sessionIdRef = useRef<string>(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const triggerBotReply = async (userQuestion: string, currentCaptchaToken?: string | null) => {
    // Abort any previous in-flight request
    if (streamingAbortRef.current) {
      streamingAbortRef.current.abort();
    }
    const controller = new AbortController();
    streamingAbortRef.current = controller;

    setIsTyping(true);
    const timeStr = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const botMsgId = "bot-" + Date.now();

    try {
      await streamChat(
        {
          message: userQuestion,
          session_id: sessionIdRef.current,
          recaptcha_token: currentCaptchaToken || undefined,
        },
        {
          signal: controller.signal,
          onStart: () => {
            setIsTyping(false);
            setIsSessionVerified(true); // Mark current session as verified by backend!
            setMessages((prev) => [
              ...prev,
              { id: botMsgId, sender: "bot", text: "", timestamp: timeStr },
            ]);
          },
          onToken: (token) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === botMsgId
                  ? { ...msg, text: msg.text + token }
                  : msg
              )
            );
          },
        }
      );
    } catch (err: unknown) {
      if (err instanceof EnergyBotApiError && err.type === "ABORTED") return;

      setIsTyping(false);
      const isRateLimit =
        err instanceof EnergyBotApiError && err.type === "RATE_LIMIT_EXCEEDED";
      const isCaptchaFail =
        err instanceof EnergyBotApiError && (err.type === "CAPTCHA_FAILED" || err.type === "CAPTCHA_REQUIRED");

      if (isCaptchaFail) {
        setIsSessionVerified(false);
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }

      const errorText = isCaptchaFail
        ? t("energy_bot.captcha_failed")
        : isRateLimit
        ? t("energy_bot.rate_limit_reply")
        : t("energy_bot.error_reply");

      // Show friendly error, rate-limit, or captcha error message in chat
      setMessages((prev) => [
        ...prev,
        {
          id: botMsgId,
          sender: "bot",
          text: errorText,
          timestamp: timeStr,
        },
      ]);
    } finally {
      setIsTyping(false);
      streamingAbortRef.current = null;
    }
  };

  const handleSendUserMessage = (textToSend?: string) => {
    const text = textToSend || inputValue.trim();
    if (!text || isTyping) return;

    const timeStr = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    // If session is NOT yet verified, require reCAPTCHA token for initial message
    if (!isSessionVerified && !captchaToken) {
      const promptMsg: Message = {
        id: "sys-" + Date.now(),
        sender: "bot",
        text: t("energy_bot.captcha_required"),
        timestamp: timeStr,
      };
      setMessages((prev) => [...prev, promptMsg]);
      return;
    }

    const currentToken = isSessionVerified ? undefined : captchaToken;

    const userMsg: Message = {
      id: "user-" + Date.now(),
      sender: "user",
      text,
      timestamp: timeStr,
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInputValue("");

    triggerBotReply(text, currentToken);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendUserMessage();
    }
  };

  return (
    <div
      className={`energy-ai-bot ${isTransitioning ? "energy-ai-bot--transitioning" : ""
        }`}
    >
      {/* ---------------- Idle Floating Particle Bot Button ---------------- */}
      {!isOpen && (
        <div
          className="energy-ai-bot__trigger"
          onClick={handleOpen}
          role="button"
          tabIndex={0}
          aria-label={t("energy_bot.tooltip")}
        >
          <canvas ref={orbCanvasRef} className="energy-ai-bot__canvas" />
          <div className="energy-ai-bot__trigger-icon">
            <img
              src={transparentLogoUrl || getImageUrl("/image/scientific/downloaded-image.png")}
              alt="WE AI Logo"
              className="energy-ai-bot__logo-img"
            />
          </div>
          <div className="energy-ai-bot__tooltip">
            <span className="energy-ai-bot__badge-dot" />
            {t("energy_bot.tooltip")}
          </div>
        </div>
      )}

      {/* ---------------- Gundam 00 GN Particle Thruster Stream ---------------- */}
      {isGnTrailActive && (
        <div
          className={`energy-ai-bot__gn-trail-overlay ${
            isPanelClosing ? "energy-ai-bot__gn-trail-overlay--closing" : ""
          }`}
        >
          <canvas ref={gnTrailCanvasRef} className="energy-ai-bot__gn-trail-canvas" />
        </div>
      )}

      {/* ---------------- Sci-Fi Chat Panel & Backdrop ---------------- */}
      {isOpen && (
        <>
          {/* Black Backdrop Wave (Expands AFTER Panel Opens, Retracts BEFORE Panel Closes) */}
          {isBackdropActive && (
            <div
              className={`energy-ai-bot__backdrop ${isBackdropClosing ? "energy-ai-bot__backdrop--closing" : ""
                }`}
              onClick={handleClose}
            >
              <canvas ref={backdropCanvasRef} className="energy-ai-bot__backdrop-canvas" />
            </div>
          )}

          {/* Chat Panel Window */}
          <div
            className={`energy-ai-bot__panel ${isPanelClosing ? "energy-ai-bot__panel--closing" : ""
              }`}
          >
            <canvas ref={bgCanvasRef} className="energy-ai-bot__panel-canvas" />

            {/* Header */}
            <div className="energy-ai-bot__header">
              <div className="energy-ai-bot__header-info">
                <div className="energy-ai-bot__header-icon">
                  <img
                    src={transparentLogoUrl || getImageUrl("/image/scientific/downloaded-image.png")}
                    alt="WE AI Logo"
                    className="energy-ai-bot__header-logo"
                  />
                </div>
                <div className="energy-ai-bot__header-title">
                  {t("energy_bot.title")}
                  <span className="energy-ai-bot__header-status">
                    <span className="energy-ai-bot__status-dot" />
                    {t("energy_bot.status")}
                  </span>
                </div>
              </div>
              <button
                className="energy-ai-bot__close-btn"
                onClick={handleClose}
                aria-label={t("buttons.close")}
              >
                ✕
              </button>
            </div>

            {/* Chat Messages Body */}
            <div className="energy-ai-bot__body" ref={bodyRef}>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`energy-ai-bot__message energy-ai-bot__message--${msg.sender}`}
                >
                  <div
                    className="energy-ai-bot__msg-content"
                    dangerouslySetInnerHTML={{
                      __html: marked.parse(msg.text || "", { async: false }) as string,
                    }}
                  />
                  <span className="energy-ai-bot__msg-time">{msg.timestamp}</span>

                  {/* Quick Preset Questions Chips inside Welcome Message */}
                  {msg.isQuickQuestions && (
                    <div className="energy-ai-bot__quick-questions">
                      {getQuickQuestions().map((item) => (
                        <button
                          key={item.key}
                          className="energy-ai-bot__chip"
                          onClick={() => handleSendUserMessage(item.q)}
                        >
                          {item.q}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* AI Typing Indicator */}
              {isTyping && (
                <div className="energy-ai-bot__message energy-ai-bot__message--bot">
                  <div className="energy-ai-bot__msg-content">
                    <div className="energy-ai-bot__thinking">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dark Theme Scaled reCAPTCHA Verification (Only required on first message per session) */}
            {!isSessionVerified && (
              <div className="energy-ai-bot__recaptcha-wrapper">
                <RecaptchaComponent
                  ref={recaptchaRef}
                  theme="dark"
                  size="normal"
                  onCaptchaChange={(token) => setCaptchaToken(token)}
                />
              </div>
            )}

            {/* Footer Input */}
            <div className="energy-ai-bot__footer">
              <div className="energy-ai-bot__input-wrapper">
                <input
                  type="text"
                  className="energy-ai-bot__input"
                  placeholder={t("energy_bot.placeholder")}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isTyping}
                />
              </div>
              <button
                className="energy-ai-bot__send-btn"
                onClick={() => handleSendUserMessage()}
                disabled={!inputValue.trim() || isTyping}
                aria-label="Send"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnergyAiBot;
